import { Helmet } from "react-helmet";
import { SideBar } from "../side-bar/SideBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faArrowUpFromBracket,
  faDownload,
  faTimes,
  faFileCirclePlus,
} from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, getToken } from "../../../services/apis/auth-api/apiUrl";
import * as formatEndPoint from "../../../config/config";
import CvTemplate from "./CvTemplate";
import { formatedCvData } from "../../../utils/types";
import Modal from "../modal/modal";
import CvJson from "../modal/cvf-modal/CvJson";

export default function FormatCv() {
  const [cvData, setCvData] = useState<formatedCvData | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [donloadingDocx, setDownloadingDocx] = useState<boolean>(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalChildren, setModalChildren] = useState<React.JSX.Element | null>(
    null,
  );
  const [file, setFile] = useState<File | null>(null);
  const [rawCvText, setRawCvText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [formatStat, setFormatStat] = useState<string>("");
  const [extractFailed, setExtractFailed] = useState<boolean>(false);
  const [isBlindCV, setIsBlindCV] = useState<boolean>(false);
  const [show, setShow] = useState(false);

  const fileInput = useRef<HTMLInputElement | null>(null);

  const handleSetFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(null);
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setFile(null);
    const file = e.dataTransfer.files[0];
    if (file) {
      setFile(file);
    }
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleOcrProcess = async () => {
    try {
      if (!file) return false;
      const formData = new FormData();
      formData.append("file", file);
      const text = await api.post(
        formatEndPoint.OCR_PROCESS_END_POINT,
        formData,
        {
          headers: {
            Authorization: "Bearer " + getToken(),
          },
        },
      );
      if (text.data.length === 0) {
        setFormatStat("No extracted text detected in the document.");
        setExtractFailed(true);
        if (fileInput.current) fileInput.current.value = "";
        setRawCvText("");
      } else {
        setRawCvText(text.data);
        setFile(null);
        if (fileInput.current) fileInput.current.value = "";
      }
    } catch (e) {
      setFormatStat("OCR process failed. Please try again.");
      setExtractFailed(true);
      if (fileInput.current) fileInput.current.value = "";
      setRawCvText("");
    }
  };

  useEffect(() => {
    const upload = async () => {
      if (!file) return false;
      const formData = new FormData();
      formData.append("file", file);

      formData.append("cv_content", JSON.stringify(cvData));

      const endpoint: string = formatEndPoint.EXTRACT_TEXT_FROM_CV;

      try {
        if (
          file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
           setFormatStat(
            "Extracting text from docx file.....",
          );
          const text = await api.post(endpoint, formData, {
            headers: {
              Authorization: "Bearer " + getToken(),
            },
          });
          setRawCvText(text.data);
          setFile(null);
          if (fileInput.current) fileInput.current.value = "";
        } else {
          const type = file.type === "application/pdf" ? "pdf" : "image";
          setFormatStat(
            `Running OCR extraction from ${type} file.....`,
          );
          setTimeout(handleOcrProcess, 3000);
        }
      } catch (e) {
        setFormatStat(
          "Text extraction failed — please use a valid pdf or docx file.",
        );
        setExtractFailed(true);
        if (fileInput.current) fileInput.current.value = "";
        setRawCvText("");
      }
    };

    if (file) {
      setLoading(true);
      setFormatStat("Checking file type.....");
      setExtractFailed(false);
      setTimeout(() => upload(), 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    const processCvRawText = async () => {
      try {
        const response = await api.post(
          formatEndPoint.AI_PROCESS_CVF,
          {
            raw_text: rawCvText,
            blindCv: isBlindCV,
          },
          {
            headers: {
              Authorization: "Bearer " + getToken(),
            },
          },
        );
        const formattedCvTxt = response.data as formatedCvData;
        setCvData(formattedCvTxt);
        setTimeout(() => {
          setLoading(false);
          setFormatStat("");
          setExtractFailed(false);
          if (fileInput.current) fileInput.current.value = "";
          setRawCvText("");
        }, 1000);
      } catch (e) {
        setFormatStat("Something went wrong — the AI didn't respond.");
        setExtractFailed(true);
        if (fileInput.current) fileInput.current.value = "";
        setRawCvText("");
      }
    };
    if (rawCvText !== "") {
      setLoading(true);
      setFormatStat("AI is processing and formatting the extracted text.....");
      setExtractFailed(false);
      setTimeout(() => processCvRawText(), 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawCvText]);

  const handleDownLoadPdf = async () => {
    // const cvObj = Object.values(cvData).every(value => value !== '');
    // if(!cvObj) return false;
    if (cvData === null) return false;
    setDownloading(true);
    try {
      const response = await api.post(
        formatEndPoint.DOWNLOAD_CV_PDF_END_POINT,
        cvData,
        {
          responseType: "blob",
          headers: { Authorization: "Bearer " + getToken() },
        },
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );

      // eslint-disable-next-line no-useless-escape
      const filename =
        (cvData.cdd_name || "default-name").replace(/\n/g, " ") + ".pdf";

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (e) {
      alert("Error downloading pdf");
      console.error("Error during PDF download:", e);
      setDownloading(false);
    }
  };

  const handleDownLoadDocx = async () => {
    // const cvObj = Object.values(cvData).every(value => value !== '');
    // if(!cvObj) return false;
    if (cvData === null) return false;
    setDownloadingDocx(true);
    try {
      const response = await api.post(
        formatEndPoint.DOWNLOAD_CV_DOCX_END_POINT,
        cvData,
        {
          responseType: "blob",
          headers: { Authorization: "Bearer " + getToken() },
        },
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }),
      );

      // eslint-disable-next-line no-useless-escape
      const filename =
        (cvData.cdd_name || "default-name").replace(/\n/g, " ") + ".docx";

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadingDocx(false);
    } catch (e) {
      alert("Error downloading docx");
      console.error("Error during DOCX download:", e);
      setDownloadingDocx(false);
    }
  };

  const createModalContent = (content: React.JSX.Element) => {
    setModalChildren(content);
    setShowModal(true);
  };

  return (
    <div className="content flex">
      <Helmet>
        <title>Format CV</title>
      </Helmet>
      <SideBar />
      <div className="main-content p-0 overflow-hidden">
        <div>
          <div className="flex flex-col gap-2 bg-transparent ">
            <div className="bg-gray-100 pt-4 shadow">
              <div className="px-4 py-2 flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isBlindCV}
                    onChange={() =>
                      (!loading && !extractFailed) || extractFailed
                        ? setIsBlindCV(isBlindCV ? false : true)
                        : null
                    }
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 uppercase">
                    Format as blind CV
                  </span>
                </label>
              </div>
              <div className="flex gap-2 h-8 px-1 mb-4 mx-2">
                <div>
                  <input
                    type="file"
                    onChange={(e) => handleSetFile(e)}
                    accept=".pdf,.docx,image/png, image/jpeg, image/jpg, image/webp, image/gif, image/bmp, image/tiff, image/svg+xml"
                    ref={fileInput}
                    className="hidden"
                    name=""
                    id=""
                  />
                  <button
                    type="button"
                    onClick={() =>
                      (!loading && !extractFailed) || extractFailed
                        ? fileInput.current?.click()
                        : null
                    }
                    className="button button-vcc-green text-sm w-fit"
                  >
                    <FontAwesomeIcon icon={faArrowUpFromBracket} /> &nbsp;
                    Upload Raw CV
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() =>
                      (!loading && !extractFailed) || extractFailed
                        ? createModalContent(
                            <CvJson
                              onSubmitJson={(data) => {
                                setCvData(data);
                                setShowModal(false);
                              }}
                            />,
                          )
                        : null
                    }
                    className="button border border-gray-700 hover:bg-gray-200 text-sm w-fit"
                  >
                    <FontAwesomeIcon icon={faFile} /> &nbsp; Upload JSON Text
                  </button>
                </div>
                {cvData && (
                  <div className="ms-auto flex gap-2">
                    {!donloadingDocx ? (
                      <>
                        <button
                          type="button"
                          onClick={handleDownLoadDocx}
                          className="button button-vcc-blue text-xs w-fit"
                        >
                          <FontAwesomeIcon icon={faDownload} /> &nbsp; Download
                          Docx
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="button button-vcc-blue text-xs w-fit bg-blue-400 hover:bg-blue-400 cursor-not-allowed"
                        >
                          <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin"
                          />{" "}
                          &nbsp; Downloading...
                        </button>
                      </>
                    )}

                    {!downloading ? (
                      <>
                        <button
                          type="button"
                          onClick={handleDownLoadPdf}
                          className="button button-red text-xs w-fit"
                        >
                          <FontAwesomeIcon icon={faDownload} /> &nbsp; Download
                          PDF
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="button button-vcc-green text-xs w-fit bg-red-400 hover:bg-red-400 cursor-not-allowed"
                        >
                          <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin"
                          />{" "}
                          &nbsp; Downloading...
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {/* loading bar */}
              {loading && (
                <div className="flex flex-col">
                  <div className="bg-white border px-4">
                    <small>
                      <FontAwesomeIcon
                        icon={extractFailed ? faTimes : faSpinner}
                        className={
                          extractFailed ? "text-red-400" : "animate-spin"
                        }
                      />{" "}
                      &nbsp;
                      {formatStat}
                    </small>
                  </div>
                  <div
                    className={`${extractFailed ? "bg-rose-400" : "bg-blue-300"} animate-pulse h-1`}
                  ></div>
                </div>
              )}
              {/* ----loading bar */}
            </div>
          </div>
        </div>

        <div className="h-full overflow-y-auto">
          <div className="relative mt-4 w-full flex justify-center">
            <CvTemplate data={cvData} />
            <div
              onMouseEnter={() => setShow(true)}
              onMouseLeave={() => setShow(false)}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="absolute flex justify-center items-center w-full h-full p-6 border-2 border-dashed rounded-lg text-center"
            >
              {show && (
                <h2 className="text-gray-300 font-semibold text-center bg-gray-500 shadow-xl px-6 py-4 rounded-2xl">
                  <FontAwesomeIcon
                    icon={faFileCirclePlus}
                    className="text-2xl"
                  />{" "}
                  &nbsp; Drag & drop CV here to upload
                </h2>
              )}
            </div>
          </div>
          <div className="h-28 bg-gray-200"></div>
        </div>
      </div>
      {showModal && (
        <Modal
          onClose={() => {
            setShowModal(false);
          }}
        >
          {modalChildren}
        </Modal>
      )}
    </div>
  );
}
